import { useContext, useState } from 'react';
import DatePicker from 'react-datepicker';
import { AuthContext } from '../context/authContext';

const parseLocalDate = (dateInput) => {
    if (!dateInput) return null;
    if (dateInput instanceof Date) return dateInput;
    if (typeof dateInput === 'string') {
        if (dateInput.includes('T')) {
            const dateObj = new Date(dateInput);
            const userTimezoneOffset = dateObj.getTimezoneOffset() * 60000;
            return new Date(dateObj.getTime() + userTimezoneOffset);
        }
        const parts = dateInput.split('T')[0].split('-');
        if (parts.length === 3) {
            const year = parseInt(parts[0], 10);
            const month = parseInt(parts[1], 10) - 1;
            const day = parseInt(parts[2], 10);
            return new Date(year, month, day);
        }
    }
    return new Date(dateInput);
};

const ObservacionesHistorial = ({
  data = [],
  onChange,
  titulo = "Historial de Observaciones",
  placeholder = "Ingrese una observación",
  maxLength = 2500
}) => {
  const { user } = useContext(AuthContext);
  const [activeRow, setActiveRow] = useState(null);

  const handleAgregar = () => {
    const nueva = {
      fecha: null,
      usuario: user || 'usuario',
      descripcion: ''
    };
    onChange([...data, nueva]);
  };

  const handleEliminar = (index) => {
    const confirmar = window.confirm(
      `⚠️ ¿Está seguro de que desea eliminar esta observación?`
    );
    if (!confirmar) return;
    onChange(data.filter((_, i) => i !== index));
  };

  const handleCambioDescripcion = (index, value) => {
    const actualizado = [...data];
    actualizado[index] = { ...actualizado[index], descripcion: value };
    onChange(actualizado);
  };

  const handleFechaChange = (index, date) => {
    const actualizado = [...data];
    actualizado[index] = { ...actualizado[index], fecha: date };
    onChange(actualizado);
  };

  const inputClasses = "w-full p-2 border rounded dark:bg-gray-700 dark:text-white dark:border-gray-600 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all";

  return (
    <div className="mt-4 mb-4">
      <div className="flex justify-between items-center mb-3">
        <div className="flex items-center gap-3">
          <h4 className="text-md font-semibold text-gray-800 dark:text-white">
            {titulo}
          </h4>
          <span className="inline-flex items-center justify-center min-w-[2rem] h-6 px-2 rounded-full text-xs font-semibold bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
            {data.length}
          </span>
        </div>
        <button
          type="button"
          onClick={handleAgregar}
          className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm font-medium transition-colors shadow-sm"
        >
          + Agregar Observación
        </button>
      </div>

      {data.length === 0 ? (
        <div className="p-6 bg-gray-50 dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-700 text-center text-gray-500 dark:text-gray-400">
          No hay observaciones registradas. Haga clic en "+ Agregar Observación" para añadir una.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <div className="max-h-[320px] overflow-y-auto border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm">
          <table className="w-full border-collapse">
            <thead>
              <tr className="bg-gray-100 dark:bg-gray-700 sticky top-0 z-10">
                <th className="border-b border-gray-300 dark:border-gray-600 px-4 py-2 w-12 text-center text-sm font-semibold text-gray-700 dark:text-gray-200">
                  N°
                </th>
                <th className="border-b border-gray-300 dark:border-gray-600 px-4 py-2 w-44 text-left text-sm font-semibold text-gray-700 dark:text-gray-200">
                  Fecha
                </th>
                <th className="border-b border-gray-300 dark:border-gray-600 px-4 py-2 text-left text-sm font-semibold text-gray-700 dark:text-gray-200">
                  Descripción
                </th>
                <th className="border-b border-gray-300 dark:border-gray-600 px-4 py-2 text-center text-sm font-semibold text-gray-700 dark:text-gray-200 w-16">
                  Acción
                </th>
              </tr>
            </thead>
            <tbody>
              {data.map((item, index) => (
                <tr
                  key={index}
                  onFocus={() => setActiveRow(index)}
                  onBlur={() => setActiveRow(null)}
                  onMouseEnter={() => setActiveRow(index)}
                  onMouseLeave={() => setActiveRow(null)}
                  title={item.usuario ? `Creado por: ${item.usuario}` : ''}
                  className={`transition-colors ${
                    activeRow === index
                      ? 'bg-yellow-50 dark:bg-yellow-900/20'
                      : 'hover:bg-gray-50 dark:hover:bg-gray-800'
                  }`}
                >
                  <td className="border border-gray-300 dark:border-gray-600 px-4 py-2 text-center">
                    <span className="font-medium text-gray-800 dark:text-white">
                      {index + 1}
                    </span>
                  </td>
                  <td className="border border-gray-300 dark:border-gray-600 px-4 py-2">
                    <DatePicker
                      selected={item.fecha ? parseLocalDate(item.fecha) : null}
                      onChange={(date) => handleFechaChange(index, date)}
                      dateFormat="dd/MM/yyyy"
                      className={inputClasses}
                      placeholderText="DD/MM/AAAA"
                      showYearDropdown
                      scrollableYearDropdown
                      yearDropdownItemNumber={15}
                    />
                  </td>
                  <td className="border border-gray-300 dark:border-gray-600 px-4 py-2">
                    <div className="relative w-full">
                      <textarea
                        value={item.descripcion || ''}
                        onChange={(e) => handleCambioDescripcion(index, e.target.value)}
                        className={`${inputClasses} resize-y min-h-[42px] max-h-[150px] pb-5`}
                        placeholder={placeholder}
                        maxLength={maxLength}
                        rows="1"
                      />
                      <div className="absolute bottom-1.5 right-2 text-[10px] text-gray-400 dark:text-gray-500 pointer-events-none">
                        {(item.descripcion || '').length}/{maxLength}
                      </div>
                    </div>
                  </td>
                  <td className="border border-gray-300 dark:border-gray-600 px-4 py-2 text-center">
                    <button
                      type="button"
                      onClick={() => handleEliminar(index)}
                      className="p-2 rounded text-red-600 hover:text-red-800 hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors"
                      title="Eliminar observación"
                    >
                      🗑️
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default ObservacionesHistorial;
